import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import SEOHead from "@/components/base/SEOHead";

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://oopbuyspreadsheet.work';

function buildShoppingGuideJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "OOPBuy Spreadsheet Shopping Guide – All 14 Categories",
    "description": "A complete walkthrough of the OOPBuy spreadsheet category system. Learn how to navigate all 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, Accessories and more.",
    "url": `${SITE_URL}/shopping-guide`,
    "about": {
      "@type": "Thing",
      "name": "OOPBuy Spreadsheet Categories",
      "description": "14 product categories organized in the OOPBuy spreadsheet: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff."
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Shopping Guide",
          "item": `${SITE_URL}/shopping-guide`
        }
      ]
    }
  };
}

export default function ShoppingGuide() {
  return (
    <main>
      <SEOHead
        title="OOPBuy Spreadsheet Shopping Guide – Browse All 14 Product Categories"
        description="A complete walkthrough of the OOPBuy spreadsheet category system. Learn how to navigate all 14 product categories, understand what each section offers, and find exactly what you are looking for in the OOPBuy spreadsheet."
        keywords="oopbuy spreadsheet shopping guide, oopbuy spreadsheet categories, oopbuy spreadsheet navigation, product category guide, oopbuy spreadsheet browse"
        canonicalPath="/shopping-guide"
        ogType="website"
        jsonLd={buildShoppingGuideJsonLd()}
      />
      <Navbar />
      <GuideContent />
      <Footer />
    </main>
  );
}

function Navbar() {
  return (
    <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="flex items-center justify-between h-16 md:h-20 px-6 md:px-10 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-database-2-line text-background-50 text-lg"></i>
          </div>
          <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 whitespace-nowrap">
            OOPBuy Spreadsheet
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </Link>
      </div>
    </nav>
  );
}

function GuideContent() {
  return (
    <article className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-10 md:mb-14">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" className="text-xs md:text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer">
            Home
          </Link>
          <span className="text-foreground-300 text-xs">/</span>
          <span className="text-xs md:text-sm text-foreground-500">Shopping Guide</span>
        </div>
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-4">
          OOPBuy Spreadsheet Shopping Guide: How to Browse and Source Products
        </h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
          A complete walkthrough of the OOPBuy spreadsheet category system. Learn how to navigate all 14 product categories, understand what each section offers, and find exactly what you are looking for.
        </p>
      </header>

      <div className="relative rounded-2xl overflow-hidden mb-10 md:mb-14 aspect-[21/9]">
        <img
          src="https://readdy.ai/api/search-image?query=Organized%20fashion%20product%20catalog%20displayed%20on%20a%20tablet%20screen%20next%20to%20shopping%20bags%20on%20a%20warm%20cream%20desk%2C%20soft%20natural%20daylight%2C%20modern%20editorial%20photography%2C%20clean%20minimalist%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20retail%20styling&width=1400&height=600&seq=shopping-guide-v2&orientation=landscape"
          alt="OOPBuy Spreadsheet Shopping Guide"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="space-y-10 md:space-y-12">
        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            How to Use the OOPBuy Spreadsheet for Shopping
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            The OOPBuy spreadsheet organizes products into 14 clearly defined categories, making it incredibly easy to find what you need. Whether you are looking for shoes, clothing, accessories, or lifestyle products, the category-based navigation system ensures you spend less time searching and more time discovering great products.
          </p>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
            Each category below links directly to organized product listings on kaspreadsheet.com. Simply click the category you are interested in, and you will be taken to a dedicated page with structured product information, verified listings, and easy-to-scan data formats.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            All 14 Product Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
              >
                <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                  <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0">
                  <span className="text-sm md:text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors block whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 mt-0.5 block">
                    {cat.isSearch ? "Search listings" : "Browse category"}
                  </span>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors ml-auto flex-shrink-0"></i>
              </a>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            Shopping Tips
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-bold text-sm flex-shrink-0 mt-0.5">1</div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground-800 mb-1">Start with Your Favorite Category</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Pick the category that interests you most — Shoes, Jackets, or Accessories — and explore it thoroughly before moving to other sections. Familiarity with one category makes navigating others much easier.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-bold text-sm flex-shrink-0 mt-0.5">2</div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground-800 mb-1">Compare Across Categories</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Many products span multiple categories. If you are looking at Hoodies and Sweaters, also check Sets and Accessories for complementary items that complete your sourcing needs.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-bold text-sm flex-shrink-0 mt-0.5">3</div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground-800 mb-1">Check Back Regularly</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  The OOPBuy spreadsheet is updated on a regular basis. Product listings are refreshed, new items are added, and categories are refined — make it a habit to visit frequently.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-50 rounded-2xl p-6 md:p-8 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
            Ready to Start Shopping?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-5 max-w-lg mx-auto">
            Browse all 14 OOPBuy spreadsheet categories and discover organized product listings that make sourcing faster and more efficient.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
          >
            <i className="ri-shopping-cart-line"></i>
            Buy It Now
          </a>
        </section>
      </div>
    </article>
  );
}

function Footer() {
  return (
    <footer className="bg-secondary-50 border-t border-background-200/70">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-database-2-line text-background-50 text-sm"></i>
            </div>
            <span className="font-heading text-base font-semibold text-foreground-900 whitespace-nowrap">
              OOPBuy Spreadsheet
            </span>
          </Link>
          <p className="text-xs text-foreground-500">
            &copy; 2026 OOPBuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
              kaspreadsheet.com
            </a>
            <Link to="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
              Home
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}